// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeroLayout1Data } from '../layout_1/hero-layout-1.component';

/**
 * Preview component for the "Hero Layout 6" used in the website builder panel. Renders a hero section with configurable image, headings, paragraph, and a call-to-action button while exposing style and content inputs for live preview.
 * @component
 * @selector app-hero-layout-6-preview
 * @example
 *   <app-hero-layout-6-preview [data]="sampleData" (buttonClick)="handleButtonClick($event)"></app-hero-layout-6-preview>
 * @input HeroLayout1Data | null data - The content model for the hero (includes title, subtitle, image object, imageUrl, buttons, etc.). Used as the primary data source for rendering text and media.
 * @input string | null headingColor - Optional CSS color value to apply to the hero heading text.
 * @input string | null paragraphColor - Optional CSS color value to apply to the hero paragraph/body text.
 * @input string | null headingFont - Optional font-family (or font token) to apply to the hero heading.
 * @input string | null bodyFont - Optional font-family (or font token) to apply to the hero body/paragraph text.
 * @input string | null primaryColor - Optional primary/theme color used for accents such as buttons.
 * @input any titleStyles - Optional inline style object to apply additional styles to the hero title element.
 * @output { link: string; event: Event } buttonClick - Emitted when a CTA/button in the hero is clicked. Payload includes the target link (if any) and the original click Event. Emission only occurs when a link is present.
 *
 * Key methods:
 * - onButtonClick(link: string | undefined, event: Event): void
 *     Handles button clicks from the template. Emits the buttonClick output with the link and event only when a link is provided.
 * - getImageSrc(): string
 *     Resolves the hero image source URL by preferring data.image.src, falling back to data.imageUrl, or returning an empty string.
 * - getImageAlt(): string
 *     Resolves the image alt text from data.image.alt or returns a sensible default ("Car Wash").
 * - getBackgroundImage(): string
 *     Returns a CSS background-image value (e.g., "url(...)") when an image source is available, otherwise returns an empty string.
 *
 * Lifecycle hooks:
 * - This preview component does not implement any Angular lifecycle hooks explicitly (e.g., OnInit) and relies on input bindings to react to changes.
 */
@Component({
  selector: 'app-hero-layout-6-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hero-layout-6.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./hero-layout-6.component.scss'],
})
export class HeroLayout6PreviewComponent {
  @Input() data: HeroLayout1Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};
  @Output() buttonClick = new EventEmitter<{ link: string; event: Event }>();

  onButtonClick(link: string | undefined, event: Event): void {
    if (link) {
      this.buttonClick.emit({ link, event });
    }
  }

  getImageSrc(): string {
    return this.data?.image?.src || this.data?.imageUrl || '';
  }

  getImageAlt(): string {
    return this.data?.image?.alt || 'Car Wash';
  }

  getBackgroundImage(): string {
    const imageSrc = this.getImageSrc();
    return imageSrc ? `url(${imageSrc})` : '';
  }
}
